import { Box, Pagination, Stack, ThemeProvider } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import PageHeader from 'components/v2/common/pageHeader';
import { EventDetailDialog } from 'views/v2/eventTracker/components/eventDetailDialog';
import { EventFilters } from 'views/v2/eventTracker/components/eventFilters';
import { EventList } from 'views/v2/eventTracker/components/eventList';

import api from 'api';
import { useActivityEventTypes } from 'hooks/useActivityEventTypes';
import { useDebouncedValue } from 'hooks/useCheckEmail';
import useTranslate from 'hooks/useTranslate';
import { setAlert } from 'redux/alert';
import appThemeV2 from 'theme/themeV2';
import type { ActivityEventItem, ActivityEventTypeOption } from 'types';
import { getErrorMessage } from 'utils/v2/errorHelper';

const PAGE_SIZE = 10;

export default function EventTrackerPage() {
    const dispatch = useDispatch();
    const { translate } = useTranslate();

    const [searchInput, setSearchInput] = useState('');
    const debouncedSearch = useDebouncedValue(searchInput, 400);
    const {
        typeOptions,
        defaultTypes,
        isPending: isTypesPending,
        isSuccess: isTypesReady,
        isError: isTypesError,
        error: typesError,
    } = useActivityEventTypes();
    const [selectedTypes, setSelectedTypes] = useState<ActivityEventTypeOption[] | null>(null);
    const activeTypes = selectedTypes ?? defaultTypes;
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [events, setEvents] = useState<ActivityEventItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedEvent, setSelectedEvent] = useState<ActivityEventItem | null>(null);

    useEffect(() => {
        if (isTypesError) {
            setLoading(false);
            dispatch(
                setAlert({
                    message: getErrorMessage(typesError, translate('failedToLoadEventTypes')),
                    type: 'error',
                })
            );
        }
    }, [dispatch, isTypesError, translate, typesError]);

    const fetchEvents = useCallback(
        async (pageVal: number) => {
            if (!isTypesReady) return;
            setLoading(true);
            try {
                const response = await api.activityEvents.getActivityEvents({
                    page: pageVal,
                    pageSize: PAGE_SIZE,
                    q: debouncedSearch.trim() || undefined,
                    event_type: activeTypes.map((type) => type.value),
                    start_date: startDate || undefined,
                    end_date: endDate || undefined,
                    sort: 'timestamp',
                    order: sortOrder,
                });
                setEvents(response.data.events || []);
                setPage(response.data.page || pageVal);
                setTotalPages(Math.max(1, response.data.totalPages || 1));
                setTotalRecords(response.data.totalRecords || 0);
            } catch (error) {
                dispatch(
                    setAlert({
                        message: getErrorMessage(error, translate('failedToLoadEvents')),
                        type: 'error',
                    })
                );
            } finally {
                setLoading(false);
            }
        },
        [
            activeTypes,
            debouncedSearch,
            dispatch,
            endDate,
            isTypesReady,
            sortOrder,
            startDate,
            translate,
        ]
    );

    useEffect(() => {
        if (!isTypesReady) return;
        void fetchEvents(1);
    }, [fetchEvents, isTypesReady]);

    const handlePageChange = (_: React.ChangeEvent<unknown>, nextPage: number) => {
        void fetchEvents(nextPage);
    };

    return (
        <ThemeProvider theme={appThemeV2}>
            <Box sx={{ width: '100%', minWidth: 0 }}>
                <PageHeader
                    title="eventTracker"
                    description="eventTrackerDescription"
                    sx={{ mb: 2 }}
                />
                <Box sx={{ mb: 2.5 }}>
                    <EventFilters
                        search={searchInput}
                        onSearchChange={setSearchInput}
                        selectedTypes={activeTypes}
                        typeOptions={typeOptions}
                        onTypesChange={setSelectedTypes}
                        startDate={startDate}
                        onStartDateChange={setStartDate}
                        endDate={endDate}
                        onEndDateChange={setEndDate}
                        sortOrder={sortOrder}
                        onSortOrderChange={setSortOrder}
                    />
                </Box>

                <EventList
                    events={events}
                    loading={isTypesPending || loading}
                    onSelect={setSelectedEvent}
                />

                {totalRecords > 0 ? (
                    <Stack direction="row" sx={{ mt: 2.5, justifyContent: 'center' }}>
                        <Pagination
                            color="primary"
                            page={page}
                            count={totalPages}
                            onChange={handlePageChange}
                            siblingCount={1}
                            boundaryCount={1}
                        />
                    </Stack>
                ) : null}

                <EventDetailDialog event={selectedEvent} onClose={() => setSelectedEvent(null)} />
            </Box>
        </ThemeProvider>
    );
}
